import { newId, restoreGame, submitGuess as engineSubmit, type WordleMutation } from '@familyhub/shared';
import { stamp } from '../platform/clock';
import { wdb, type WordleGameRecord } from './db';
import { wordleSync } from './sync';
import { LEXICON } from './words';
import { householdTarget } from './target';
import { todayPuzzleDate } from './datetime';

/**
 * The wordle write actions.
 *
 * Every change takes the same route as the other modules: apply it locally so
 * the screen updates at once, queue the mutation, and let the sync runner replay
 * it. A game is one member's own board, so two members writing their own games
 * never touch the same row and never conflict.
 *
 * **The row is created on the first guess, not on open.** Opening the app should
 * not mark you as "playing" (nor satisfy the evening reminder's "has engaged"
 * gate) — only a real guess does.
 */

async function writeGame(record: WordleGameRecord): Promise<void> {
  const stamped: WordleGameRecord = {
    ...record,
    updated_at: stamp(),
    deleted_at: record.deleted_at ?? null,
  };
  const mutation = {
    op: 'upsert',
    table: 'wordle_games',
    id: newId(),
    row: stamped,
  } as unknown as WordleMutation;
  await wdb.transaction('rw', [wdb.games, wdb.outbox], async () => {
    await wdb.games.put(stamped);
    await wdb.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
  void wordleSync.sync();
}

/**
 * Submit a guess for today's daily word. Rebuilds the game from the stored row
 * (or a fresh one on the first guess), runs the engine — which **throws
 * `INVALID_WORD` / `GAME_OVER`** so the caller must handle a rejected guess —
 * then persists the appended guess and the new status.
 */
export async function submitDailyGuess(userId: string, guess: string): Promise<WordleGameRecord> {
  const puzzle_date = todayPuzzleDate();
  // Every row for today, not only mine: if the household has already started
  // this puzzle, their answer is the answer. See `householdTarget` — the word
  // lists ship in the bundle, so a phone one deploy behind would otherwise
  // compute a different word and never find out.
  const today = await wdb.games.where('puzzle_date').equals(puzzle_date).toArray();
  const existing = today.find((g) => !g.deleted_at && g.created_by === userId) ?? null;
  const target = householdTarget(today, userId, puzzle_date);

  const state = restoreGame(target, existing?.guesses ?? []);
  const { state: next } = engineSubmit(state, guess, LEXICON);

  const record: WordleGameRecord = {
    id: existing?.id ?? newId(),
    puzzle_date,
    target_word: target,
    guesses: next.guesses,
    status: next.status,
    created_by: userId,
    created_at: existing?.created_at,
    updated_at: '', // stamped in writeGame
    deleted_at: null,
  };
  await writeGame(record);
  return record;
}
